import BigNumber from 'bignumber.js';

import { Market, PerpetualMarket } from '#/api/markets';
import { MarketSummary, PerpetualMarketSummary } from '#/api/markets-summary';

import * as MarketFn from '#/features/market/functions';
import * as OrderFn from '#/features/order/functions';
import * as BboFn from '#/features/perpetuals/bbo/functions';
import { OpenOrdersStoreView } from '#/features/perpetuals/trade-overview/open-orders-context';

import type { Bbo } from '#/api/bbo';
import type { OrderFormState } from './form-state';

export function getPriceByOrderType(
  state: Pick<OrderFormState, 'type' | 'limitPrice' | 'triggerPrice'>,
  marketSummary: MarketSummary,
): BigNumber | null {
  switch (state.type) {
    case 'LIMIT':
    case 'STOP_LIMIT':
      return state.limitPrice;
    case 'MARKET':
      return marketSummary.mark_price ?? null;
    case 'STOP_MARKET':
      return state.triggerPrice;
    // no default
  }
}

export function convertAmountToQuoteAsset(
  // WIP: investigate refactor to do not call function with potential null `asset`
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  market: Market,
): BigNumber {
  if (asset == null) {
    return amount;
  }

  if (asset === market.base_currency) {
    return amount.multipliedBy(price);
  }

  if (asset === market.quote_currency) {
    return amount;
  }

  throw new Error(
    `${asset} does not match market ${market.base_currency}/${market.quote_currency} asset`,
  );
}

export function convertAmountToBaseAsset(
  // WIP: investigate refactor to do not call function with potential null `asset`
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  activeMarket: Market,
): BigNumber | null {
  if (asset == null) {
    return amount;
  }

  if (asset === activeMarket.base_currency) {
    return amount;
  }

  if (asset === activeMarket.quote_currency) {
    const amountBaseAsset = amount.dividedBy(price);
    const value = amountBaseAsset.isFinite()
      ? amountBaseAsset.decimalPlaces(
          MarketFn.baseCurrencyDecimalPlaces(activeMarket),
        )
      : null;

    return value;
  }

  throw new Error(
    `${asset} does not match market ${activeMarket.base_currency}/${activeMarket.quote_currency} asset`,
  );
}

export function mid(
  activeMarket: Pick<Market, 'price_tick_size'>,
  bbo: Pick<Bbo, 'ask' | 'bid'>,
): BigNumber | null {
  if (bbo.ask.isZero() || !bbo.ask.isFinite()) return null;
  if (bbo.bid.isZero() || !bbo.bid.isFinite()) return null;

  const bboMid = BboFn.mid(bbo);
  if (!bboMid.isFinite()) return null;

  return MarketFn.roundToQuoteDecimalPlaces(activeMarket, bboMid);
}

export function minAmountInQuoteAsset(
  market: Pick<Market, 'min_notional' | 'price_tick_size'>,
) {
  const valueRaw = market.min_notional;
  const value = MarketFn.roundToQuoteDecimalPlaces(
    market,
    valueRaw,
    BigNumber.ROUND_UP,
  );
  const formatted = value.toFormat(
    MarketFn.quoteCurrencyDecimalPlaces(market),
    BigNumber.ROUND_UP,
  );
  return { value, formatted };
}

export function minAmountInBaseAsset(
  market: Pick<Market, 'min_notional' | 'order_size_increment'>,
  marketSummary: Pick<MarketSummary, 'mark_price'>,
) {
  if (marketSummary.mark_price == null) {
    return null;
  }
  const valueRaw = OrderFn.convertAmountToBaseAsset(
    market.min_notional,
    marketSummary.mark_price,
  );
  const value = MarketFn.roundToBaseDecimalPlaces(
    market,
    valueRaw,
    BigNumber.ROUND_UP,
  );
  const formatted = value.toFormat(
    MarketFn.baseCurrencyDecimalPlaces(market),
    BigNumber.ROUND_UP,
  );
  return { value, formatted };
}

export function estimateLimitPriceForLeverageCalculation(
  orderFormState: OrderFormState,
  market: PerpetualMarket,
  marketSummary: PerpetualMarketSummary,
): BigNumber | null {
  switch (orderFormState.type) {
    case 'LIMIT': {
      return orderFormState.limitPrice;
    }
    case 'STOP_LIMIT': {
      return orderFormState.limitPrice;
    }
    case 'MARKET': {
      if (marketSummary.mark_price == null) return null;
      // mark price * (1 +/- price band)
      return marketSummary.mark_price.multipliedBy(
        orderFormState.side === 'BUY'
          ? BigNumber(1).plus(market.price_bands_width)
          : BigNumber(1).minus(market.price_bands_width),
      );
    }
    case 'STOP_MARKET': {
      if (orderFormState.triggerPrice == null) return null;
      // trigger price * (1 +/- price band)
      return orderFormState.triggerPrice.multipliedBy(
        orderFormState.side === 'BUY'
          ? BigNumber(1).plus(market.price_bands_width)
          : BigNumber(1).minus(market.price_bands_width),
      );
    }
    default: {
      throw new Error(
        `Unsupported order type: '${String(orderFormState.type)}'`,
      );
    }
  }
}

export function isLeverageDisabled(
  formState: OrderFormState,
  ordersStore: OpenOrdersStoreView,
) {
  // WIP: `STOP` orders margin calculation is not yet supported
  const openOrders = ordersStore.getAll();
  if (openOrders.some(OrderFn.isStopOrder)) return true;

  switch (formState.type) {
    case 'LIMIT':
      if (formState.limitPrice == null) return true;
      break;
    case 'STOP_LIMIT':
      if (formState.limitPrice == null) return true;
      if (formState.triggerPrice == null) return true;
      break;
    case 'STOP_MARKET':
      if (formState.triggerPrice == null) return true;
      break;
    case 'MARKET':
    default:
      return false;
  }

  return false;
}
